/*
  Bath & Spa Parts Online - Water Safety Search
  Asset: bspo-water-safety-search.js

  Search strategy:
  - Natural-language query cleanup
  - Synonym normalization
  - Strong intent detection
  - Intent-first filtering for distinctive safety questions
  - Generic keyword scoring only when no strong intent is present
*/

(function () {
  'use strict';

  var STOP_WORDS = {
    "a":1,"an":1,"and":1,"are":1,"around":1,"at":1,"be":1,"because":1,"by":1,
    "can":1,"could":1,"did":1,"do":1,"does":1,"for":1,"from":1,"get":1,"go":1,
    "had":1,"has":1,"have":1,"how":1,"i":1,"if":1,"in":1,"into":1,"is":1,
    "it":1,"its":1,"itself":1,"me":1,"my":1,"of":1,"on":1,"or":1,"our":1,
    "should":1,"that":1,"the":1,"their":1,"there":1,"this":1,"to":1,"use":1,
    "using":1,"was":1,"we":1,"what":1,"when":1,"where":1,"which":1,"who":1,
    "why":1,"will":1,"with":1,"would":1,"you":1,"your":1,

    /* Generic site-context words: useful for context, poor for relevance. */
    "hot":1,"tub":1,"spa":1,"pool":1,"water":1,"safe":1,"safety":1
  };

  var CONTEXT_WORDS = {
    "pool":1,"spa":1,"hot":1,"tub":1,"water":1,"safe":1,"safety":1
  };

  var SYNONYMS = {
    "dogs":"dog",
    "puppy":"dog",
    "puppies":"dog",
    "canine":"dog",
    "canines":"dog",

    "cats":"cat",
    "kitten":"cat",
    "kittens":"cat",
    "feline":"cat",
    "felines":"cat",

    "pets":"pet",
    "animals":"animal",

    "kid":"child",
    "kids":"child",
    "children":"child",
    "toddler":"child",
    "toddlers":"child",
    "baby":"child",
    "babies":"child",

    "pregnant":"pregnancy",

    "seniors":"senior",
    "elderly":"senior",
    "older":"senior",
    "grandma":"senior",
    "grandmother":"senior",
    "grandmothers":"senior",
    "grandpa":"senior",
    "grandfather":"senior",
    "grandfathers":"senior",
    "grandparent":"senior",
    "grandparents":"senior",

    "meds":"medication",
    "medicine":"medication",
    "medicines":"medication",
    "prescription":"medication",
    "prescriptions":"medication",
    "drug":"medication",
    "drugs":"medication",

    "smelly":"smell",
    "stinky":"smell",
    "odor":"smell",
    "odors":"smell",

    "murky":"cloudy",
    "milky":"cloudy",
    "hazy":"cloudy",

    "foaming":"foam",
    "foamy":"foam",

    "slime":"slimy",
    "biofilm":"slimy",

    "flakes":"flake",
    "particles":"particle",

    "algae":"green",

    "lightning":"thunderstorm",
    "storm":"thunderstorm",

    "drinking":"alcohol",
    "beer":"alcohol",
    "wine":"alcohol",
    "liquor":"alcohol",

    "airbnb":"rental",
    "hotel":"rental",
    "hotels":"rental",
    "rentals":"rental",

    "breathing":"respiratory",
    "cough":"respiratory",
    "lungs":"respiratory",
    "legionella":"respiratory",
    "legionnaires":"respiratory",

    "pseudomonas":"rash",
    "folliculitis":"rash",

    "electricity":"electrical",
    "shock":"electrical",
    "electrocution":"electrical",

    "suction":"drain",
    "entrapment":"drain",
    "vgb":"drain",

    "overheating":"temperature",
    "degrees":"temperature",

    "falling":"fall",
    "falls":"fall",
    "slips":"slip"
  };

  /*
    Strong intents are deliberately based on distinctive vocabulary.
    Generic words like "safe", "pool", "spa", and "water" never create
    a strong intent by themselves.
  */
  var INTENTS = [
    {
      id: "pets",
      queryTerms: ["dog","cat","pet","animal"],
      itemTerms: ["dog","cat","pet","animal"]
    },
    {
      id: "children",
      queryTerms: ["child"],
      itemTerms: ["child","children","kids","family"]
    },
    {
      id: "pregnancy",
      queryTerms: ["pregnancy"],
      itemTerms: ["pregnancy","pregnant","trimester","fetus"]
    },
    {
      id: "senior",
      queryTerms: ["senior"],
      itemTerms: ["senior","elderly","older adults","aging"]
    },
    {
      id: "medication",
      queryTerms: ["medication"],
      itemTerms: ["medication","medications","medicine","prescription","pharmacist","diuretic","sedative"]
    },
    {
      id: "rash",
      queryTerms: ["rash"],
      itemTerms: ["rash","pseudomonas","folliculitis","skin"]
    },
    {
      id: "respiratory",
      queryTerms: ["respiratory"],
      itemTerms: ["respiratory","legionella","legionnaires","cough","breathing","mist"]
    },
    {
      id: "drain",
      queryTerms: ["drain"],
      itemTerms: ["drain","suction","entrapment","vgb"]
    },
    {
      id: "electrical",
      queryTerms: ["electrical","gfci","breaker","wiring"],
      itemTerms: ["electrical","gfci","breaker","wiring","shock","bonding","grounding"]
    },
    {
      id: "thunderstorm",
      queryTerms: ["thunderstorm"],
      itemTerms: ["thunderstorm","lightning","storm"]
    },
    {
      id: "alcohol",
      queryTerms: ["alcohol"],
      itemTerms: ["alcohol","drinking","beer","wine","liquor"]
    },
    {
      id: "contamination",
      queryTerms: ["vomit","feces","blood","diarrhea","contamination"],
      itemTerms: ["vomit","feces","blood","diarrhea","contamination","bodily fluid"]
    },
    {
      id: "smell",
      queryTerms: ["smell","sulfur","musty"],
      itemTerms: ["smell","odor","sulfur","musty","rotten egg","chlorine smell"]
    },
    {
      id: "cloudy",
      queryTerms: ["cloudy"],
      itemTerms: ["cloudy","murky","milky","hazy"]
    },
    {
      id: "green",
      queryTerms: ["green","copper","iron"],
      itemTerms: ["green","algae","copper","iron","metals"]
    },
    {
      id: "foam",
      queryTerms: ["foam","soap","detergent"],
      itemTerms: ["foam","foamy","foaming","soap","detergent","bubbles"]
    },
    {
      id: "slimy",
      queryTerms: ["slimy","mold","mildew"],
      itemTerms: ["slimy","slime","biofilm","mold","mildew","sticky"]
    },
    {
      id: "flakes",
      queryTerms: ["flake","particle","calcium","scale"],
      itemTerms: ["flake","flakes","particle","particles","calcium","scale","white"]
    },
    {
      id: "temperature",
      queryTerms: ["temperature","104","heat"],
      itemTerms: ["temperature","104","heat","overheating","too hot"]
    },
    {
      id: "falls",
      queryTerms: ["slip","fall","cover","handrail","steps"],
      itemTerms: ["slip","fall","cover","handrail","steps","wet deck"]
    },
    {
      id: "rental",
      queryTerms: ["rental"],
      itemTerms: ["airbnb","rental","hotel","vacation","shared spa","public spa"]
    },
    {
      id: "first-aid",
      queryTerms: ["first","aid","cpr","emergency"],
      itemTerms: ["first aid","cpr","emergency","kit"]
    }
  ];

  function normalize(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/['’]/g, "")
      .replace(/&/g, " and ")
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function rawTokens(value) {
    return normalize(value).split(" ").filter(Boolean);
  }

  function normalizeToken(token) {
    return SYNONYMS[token] || token;
  }

  function meaningfulTokens(value) {
    var out = [];

    rawTokens(value).forEach(function (token) {
      token = normalizeToken(token);

      if (!token || STOP_WORDS[token]) return;
      if (out.indexOf(token) === -1) out.push(token);

      /* Pet semantic expansion. */
      if (token === "dog" || token === "cat" || token === "animal") {
        if (out.indexOf("pet") === -1) out.push("pet");
      }
    });

    return out;
  }

  function contextTokens(value) {
    var out = [];
    rawTokens(value).forEach(function (token) {
      if (CONTEXT_WORDS[token] && out.indexOf(token) === -1) out.push(token);
    });
    return out;
  }

  function containsTerm(text, term) {
    var nt = normalize(text);
    var nterm = normalize(term);

    if (!nt || !nterm) return false;

    /* Multi-word terms use direct phrase containment. */
    if (nterm.indexOf(" ") !== -1) {
      return (" " + nt + " ").indexOf(" " + nterm + " ") !== -1;
    }

    var list = rawTokens(nt).map(normalizeToken);

    if (list.indexOf(nterm) !== -1) return true;

    /* Allow pet expansion on item text too. */
    if (nterm === "pet") {
      return list.indexOf("dog") !== -1 ||
             list.indexOf("cat") !== -1 ||
             list.indexOf("animal") !== -1 ||
             list.indexOf("pet") !== -1;
    }

    return false;
  }

  function detectIntents(query) {
    var qTokens = meaningfulTokens(query);

    return INTENTS.map(function (intent) {
      var hits = 0;

      intent.queryTerms.forEach(function (term) {
        var nt = normalizeToken(term);
        if (qTokens.indexOf(nt) !== -1 || containsTerm(query, term)) {
          hits++;
        }
      });

      return {
        id: intent.id,
        hits: hits,
        strength: hits > 0 ? 100 + ((hits - 1) * 25) : 0,
        intent: intent
      };
    }).filter(function (match) {
      return match.hits > 0;
    }).sort(function (a, b) {
      return b.strength - a.strength;
    });
  }

  function itemText(item) {
    return normalize(
      (item.getAttribute("data-ws") || "") + " " +
      (item.textContent || "")
    );
  }

  function itemTitleText(item) {
    var title = item.querySelector("h3");
    if (title) return normalize(title.textContent || "");
    return normalize(item.textContent || "");
  }

  function itemMatchesIntent(item, intent) {
    var text = itemText(item);

    return intent.itemTerms.some(function (term) {
      return containsTerm(text, term);
    });
  }

  function scoreGeneric(item, query) {
    var qTokens = meaningfulTokens(query);
    var itemTokens = meaningfulTokens(itemText(item));
    var titleTokens = meaningfulTokens(itemTitleText(item));
    var qContext = contextTokens(query);
    var itemContext = contextTokens(itemText(item));

    var score = 0;

    qTokens.forEach(function (token) {
      if (titleTokens.indexOf(token) !== -1) {
        score += 32;
      } else if (itemTokens.indexOf(token) !== -1) {
        score += 18;
      }
    });

    qContext.forEach(function (token) {
      if (itemContext.indexOf(token) !== -1) score += 2;
    });

    var nq = normalize(query);
    var title = itemTitleText(item);
    var haystack = itemText(item);

    if (nq && title && title.indexOf(nq) !== -1) score += 80;
    else if (nq && haystack && haystack.indexOf(nq) !== -1) score += 45;

    return score;
  }

  function scoreIntentItem(item, query, matches) {
    var score = scoreGeneric(item, query);
    var title = itemTitleText(item);

    matches.forEach(function (match, index) {
      if (!itemMatchesIntent(item, match.intent)) return;

      score += index === 0 ? 180 : 95;
      score += match.hits * 25;

      /* Title-level intent match deserves another boost. */
      if (match.intent.itemTerms.some(function (term) {
        return containsTerm(title, term);
      })) {
        score += 65;
      }
    });

    return score;
  }

  function init() {
    var root = document.getElementById("bspoWaterSafety");
    var input = document.getElementById("bspoWsSearch");
    var clear = document.getElementById("bspoWsClear");
    var count = document.getElementById("bspoWsResultCount");
    var empty = document.getElementById("bspoWsEmpty");

    if (!root || !input || !clear || !count || !empty) return;

    var items = Array.prototype.slice.call(
      root.querySelectorAll(".bspo-ws-filter-item")
    );

    var sections = Array.prototype.slice.call(
      root.querySelectorAll("[data-ws-section]")
    );

    items.forEach(function (item, index) {
      item.setAttribute("data-ws-original-order", String(index));
    });

    function updateSubheads(queryActive) {
      var subheads = Array.prototype.slice.call(
        root.querySelectorAll(".bspo-ws-subhead")
      );

      subheads.forEach(function (subhead) {
        var grid = subhead.nextElementSibling;
        if (!grid || !grid.classList.contains("bspo-ws-article-grid")) return;

        if (!queryActive) {
          subhead.classList.remove("bspo-ws-filter-hidden");
          return;
        }

        var articleItems = Array.prototype.slice.call(
          grid.querySelectorAll(".bspo-ws-filter-item")
        );

        var hasVisible = articleItems.some(function (item) {
          return !item.classList.contains("bspo-ws-filter-hidden");
        });

        subhead.classList.toggle("bspo-ws-filter-hidden", !hasVisible);
      });
    }

    function updateSections() {
      sections.forEach(function (section) {
        var sectionItems = Array.prototype.slice.call(
          section.querySelectorAll(".bspo-ws-filter-item")
        );

        if (!sectionItems.length) return;

        var hasVisible = sectionItems.some(function (item) {
          return !item.classList.contains("bspo-ws-filter-hidden");
        });

        section.classList.toggle("bspo-ws-filter-hidden", !hasVisible);
      });
    }

    function resetPage() {
      items.forEach(function (item) {
        item.classList.remove("bspo-ws-filter-hidden");
        item.removeAttribute("data-ws-score");
      });

      sections.forEach(function (section) {
        section.classList.remove("bspo-ws-filter-hidden");
      });

      updateSubheads(false);
      count.textContent = "Showing all safety resources.";
      empty.classList.remove("is-visible");
    }

    function filterPage() {
      var query = input.value || "";
      var nq = normalize(query);

      if (!nq) {
        resetPage();
        return;
      }

      var intentMatches = detectIntents(query);
      var scored = [];

      items.forEach(function (item) {
        var score = intentMatches.length
          ? scoreIntentItem(item, query, intentMatches)
          : scoreGeneric(item, query);

        scored.push({
          item: item,
          score: score,
          primaryIntentMatch: intentMatches.length
            ? itemMatchesIntent(item, intentMatches[0].intent)
            : false,
          anyIntentMatch: intentMatches.some(function (m) {
            return itemMatchesIntent(item, m.intent);
          })
        });
      });

      /*
        Strong intent query:
        - Only show resources that actually match a detected intent.
        - This prevents a generic word like "pool" or "safe" from keeping
          unrelated senior, alcohol, medication, etc. cards visible.
      */
      if (intentMatches.length) {
        scored.forEach(function (row) {
          var show = row.anyIntentMatch && row.score >= 120;

          row.item.setAttribute("data-ws-score", String(row.score));
          row.item.classList.toggle("bspo-ws-filter-hidden", !show);
        });

        /*
          If an unusually specific query only finds one strong resource,
          allow up to two high-scoring supporting resources.
        */
        var strongVisible = scored.filter(function (row) {
          return !row.item.classList.contains("bspo-ws-filter-hidden");
        });

        if (strongVisible.length < 2) {
          scored
            .filter(function (row) {
              return row.item.classList.contains("bspo-ws-filter-hidden") &&
                     row.score >= 55;
            })
            .sort(function (a, b) {
              return b.score - a.score;
            })
            .slice(0, 2 - strongVisible.length)
            .forEach(function (row) {
              row.item.classList.remove("bspo-ws-filter-hidden");
            });
        }
      } else {
        /*
          Generic question with no distinctive intent:
          fall back to meaningful-keyword relevance.
        */
        scored.forEach(function (row) {
          var show = row.score >= 38;
          row.item.setAttribute("data-ws-score", String(row.score));
          row.item.classList.toggle("bspo-ws-filter-hidden", !show);
        });
      }

      updateSections();
      updateSubheads(true);

      var visible = items.filter(function (item) {
        return !item.classList.contains("bspo-ws-filter-hidden");
      }).length;

      count.textContent = visible === 1
        ? "1 safety resource matched."
        : visible + " safety resources matched.";

      empty.classList.toggle("is-visible", visible === 0);
    }

    input.addEventListener("input", filterPage);

    clear.addEventListener("click", function () {
      input.value = "";
      filterPage();
      input.focus();
    });

    filterPage();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
